/**
 * CookNotes — the notes drawer inside cook mode (Steg F4). It reuses the F6
 * "Notater" (comments) API so notes written here and in the read view are one
 * and the same. It is CONTROLLED: the parent (CookSession) owns the fetched
 * notes so the header's note-count badge and this list never diverge; this
 * component only renders them + owns the add-note form. Deliberately slim — no
 * likes/delete (those live in the read view's fuller Notater section).
 *
 * Security: note bodies render as React text nodes (never dangerouslySetInnerHTML);
 * the author label is a display name or a generic fallback, never a subject id.
 */

import type { RecipeComment } from "@tjommi/shared";
import React, { useState } from "react";

import { UnauthorizedError } from "../api";
import { formatRelativeTime } from "../lib/formatDate";
import { initialsFromName } from "../lib/initials";

function authorLabel(displayName: string | null): string {
  return displayName?.trim() || "Medlem";
}

export function CookNotes({
  notes,
  loaded,
  error,
  onAdd,
  onClose,
  onSessionExpired,
}: {
  notes: RecipeComment[];
  /** True once the initial fetch settled (whether ok or error). */
  loaded: boolean;
  /** True when the initial fetch failed. */
  error: boolean;
  /** Persist a new note; rejects on failure (Unauthorized bubbles up). */
  onAdd: (body: string) => Promise<void>;
  onClose: () => void;
  onSessionExpired: () => void;
}): React.JSX.Element {
  const [draft, setDraft] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    const body = draft.trim();
    if (!body || submitting) return;
    setSubmitting(true);
    setFormError(null);
    try {
      await onAdd(body);
      setDraft("");
    } catch (err) {
      if (err instanceof UnauthorizedError) return onSessionExpired();
      setFormError("Kunne ikke lagre notatet. Prøv igjen.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <aside className="cook-notes" data-testid="cook-notes" aria-label="Notater">
      <div className="cook-notes-head">
        <h2>Notater</h2>
        <button
          type="button"
          className="cook-notes-close"
          data-testid="cook-notes-close"
          aria-label="Lukk notater"
          onClick={onClose}
        >
          ×
        </button>
      </div>

      {!loaded && <p className="cook-msg">Laster notater …</p>}
      {loaded && error && <p className="cook-msg">Kunne ikke laste notater.</p>}

      {loaded && !error && (
        <>
          {notes.length === 0 && (
            <p className="cook-msg" data-testid="cook-notes-empty">
              Ingen notater ennå.
            </p>
          )}
          <div className="cook-notes-list" data-testid="cook-notes-list">
            {notes.map((n) => (
              <div className="cook-note" key={n.id} data-testid={`cook-note-${n.id}`}>
                <div className="cook-note-av" aria-hidden="true">
                  {initialsFromName(n.authorDisplayName)}
                </div>
                <div className="cook-note-body">
                  <div className="cook-note-head">
                    <b>{authorLabel(n.authorDisplayName)}</b>
                    <span className="cook-note-time">{formatRelativeTime(n.createdAt)}</span>
                  </div>
                  <p className="cook-note-text">{n.body}</p>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      <form
        className="cook-note-form"
        data-testid="cook-note-form"
        onSubmit={(e) => void handleSubmit(e)}
      >
        <textarea
          className="sub-input"
          data-testid="cook-note-textarea"
          placeholder="Dine observasjoner og tips …"
          rows={3}
          value={draft}
          disabled={submitting}
          onChange={(e) => setDraft(e.target.value)}
        />
        {formError && (
          <p className="cook-msg" role="alert" data-testid="cook-note-error">
            {formError}
          </p>
        )}
        <button
          type="submit"
          className="btn btn-primary"
          data-testid="cook-note-submit"
          disabled={submitting || draft.trim() === ""}
        >
          {submitting ? "Lagrer …" : "Lagre notat"}
        </button>
      </form>
    </aside>
  );
}
